import { useEffect, useRef } from "react";

const STORAGE_ON = "sat_click_sfx_on";
const DEFAULT_ON = true;

/** Tone presets keyed by variant. `default` is the neutral tik. */
const VARIANTS = {
  default: { hi: 1200, lo: 700,  sub: 140, gain: 0.18, dur: 0.09 },
  primary: { hi: 1600, lo: 950,  sub: 180, gain: 0.22, dur: 0.11 },
  soft:    { hi: 900,  lo: 620,  sub: 110, gain: 0.11, dur: 0.07 },
};

/** Resolve which variant an element should play. */
function pickVariant(el) {
  const explicit = el.getAttribute("data-sfx-variant");
  if (explicit && VARIANTS[explicit]) return explicit;
  // Primary: solid buttons, submit inputs, form primary buttons.
  // Use `el.type` (IDL property) — reflects the default 'submit' for a
  // <button> inside a <form> without an explicit type attribute.
  if (
    el.classList?.contains("sat-btn-solid") ||
    (el.tagName === "BUTTON" && el.type === "submit") ||
    (el.tagName === "INPUT" && el.type === "submit")
  ) return "primary";
  // Soft: links, tab-like nav items.
  if (
    el.tagName === "A" ||
    el.getAttribute("role") === "tab" ||
    el.getAttribute("role") === "menuitem"
  ) return "soft";
  return "default";
}

/** Interactive elements that should get a "tik" on click. */
const CLICKABLE_SELECTOR = [
  "button",
  "a[href]",
  '[role="button"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[data-clickable]',
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="checkbox"]',
  'input[type="radio"]',
  "summary",
  "label[for]",
  "[data-testid$='-card']",
  "article[data-testid]",
].join(", ");

export function isClickSfxOn() {
  const raw = localStorage.getItem(STORAGE_ON);
  return raw === null ? DEFAULT_ON : raw === "1";
}
export function setClickSfxOn(on) {
  localStorage.setItem(STORAGE_ON, on ? "1" : "0");
  window.dispatchEvent(new Event("sat:sfx-changed"));
}

/**
 * Global click SFX — plays a short synthesised "tik" whenever the user
 * activates an interactive element. No asset files, no external deps.
 * Enable/disable via the toggle in the AmbientSound panel.
 */
export default function ClickSound() {
  const ctxRef = useRef(null);

  useEffect(() => {
    // Lazy-init AudioContext on first user gesture (required by browsers).
    const ensureCtx = () => {
      if (ctxRef.current) return ctxRef.current;
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        ctxRef.current = new AC();
        return ctxRef.current;
      } catch { return null; }
    };

    const playTik = (variantKey = "default") => {
      const ctx = ensureCtx();
      if (!ctx) return;
      // Resume if suspended (Chrome/Safari after inactivity).
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      const v = VARIANTS[variantKey] || VARIANTS.default;

      // Layered click: high-frequency blip + subtle low thump for weight.
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(v.hi, now);
      osc.frequency.exponentialRampToValueAtTime(v.lo, now + v.dur * 0.55);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(v.gain, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + v.dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + v.dur + 0.01);

      // Sub-thump for a satisfying weight.
      const sub = ctx.createOscillator();
      const subGain = ctx.createGain();
      sub.type = "sine";
      sub.frequency.setValueAtTime(v.sub, now);
      subGain.gain.setValueAtTime(0.0001, now);
      subGain.gain.exponentialRampToValueAtTime(v.gain * 0.55, now + 0.003);
      subGain.gain.exponentialRampToValueAtTime(0.0001, now + Math.max(0.04, v.dur * 0.7));
      sub.connect(subGain).connect(ctx.destination);
      sub.start(now);
      sub.stop(now + Math.max(0.05, v.dur * 0.75));
    };

    const onClick = (e) => {
      if (!isClickSfxOn()) return;
      if (e.button && e.button !== 0) return; // only primary
      // Skip disabled elements.
      const t = e.target;
      if (!(t instanceof Element)) return;
      const el = t.closest(CLICKABLE_SELECTOR);
      if (!el) return;
      if (el.hasAttribute("disabled") || el.getAttribute("aria-disabled") === "true") return;
      // Skip the SFX toggle itself and volume slider drags so toggle sound
      // isn't disorienting.
      if (el.closest('[data-sfx-silent]')) return;
      playTik(pickVariant(el));
    };

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  // Close AudioContext on unmount to free resources.
  useEffect(() => () => {
    if (ctxRef.current) {
      try { ctxRef.current.close(); } catch { /* ignore */ }
      ctxRef.current = null;
    }
  }, []);

  return null;
}
